/**
 * Shared `<Table>` styling for V4 list tables that pin a sticky right-hand actions
 * column. Spread into each table's own `sx` (which keeps its own
 * width / minWidth / tableLayout).
 *
 * A sticky cell drops its border under `border-collapse: collapse`, so switch to
 * `border-collapse: separate` — that lets each sticky cell paint its own
 * `borderBottom` (the header divider) without a box-shadow hack.
 *
 * Every sticky-actions table wants this. The last-column stretch that used to
 * live here does not — see `elasticLastColumnSx`.
 */
export const stickyActionsTableSx = {
  borderCollapse: 'separate' as const,
  borderSpacing: 0,
};

/** The selector the stretch below is keyed on — exported so the structural
 *  guard in `tableStyles.tests.ts` can assert on it by name. */
export const LAST_DATA_COLUMN_SELECTOR =
  '& > thead > tr > th:nth-last-of-type(2), & > tbody > tr > td:nth-last-of-type(2)';

/**
 * Stretch the last data column so it absorbs any slack, keeping a table from
 * ending short of its container and leaving a white band beside the sticky
 * actions column.
 *
 * ONLY spread this into a sizing branch whose last data column is either
 * unsized or sized in pixels:
 *  - an auto-layout table (`width: max-content`, no colgroup); or
 *  - a frozen fixed-layout table whose colgroup deliberately leaves the last
 *    data column width-less and gives every other column a pixel width.
 *
 * NEVER spread it into a fixed-layout branch whose colgroup sizes every column,
 * because `buildColLayout` sizes flex columns with a percentage `calc()`. This
 * `width: 100%` does NOT lose to such a `<col>` — it beats it, the last data
 * column claims the whole table, and every other percentage-sized column
 * collapses to exactly 0px. Pixel-sized columns survive, so the table looks like
 * it has arbitrarily lost its wide columns.
 *
 * That is the Shipments bug: the default last column (`qtyTotal`) is 90px, so
 * the stretch was inert and nothing was wrong; ticking "Completed At" — or any
 * other flex column that sorts after it — made a percentage column last and
 * collapsed Created At, Source, Destination, Client/Contract and Connote to 0px.
 * Untick and they came back. A fixed-layout table with a fully-sized colgroup
 * already fills its container, so it needs no stretch to begin with.
 *
 * `tableStyles.tests.ts` enforces that split; `e2e/tests/list-layout.spec.ts`
 * measures the real geometry, which jsdom cannot.
 */
// Known limitation: `nth-last-of-type(2)` assumes a trailing actions column.
// Client/customer views omit it, so on those the stretch lands on the
// second-to-last data column instead. Only visible before the table freezes to
// its fixed-pixel model (the frozen colgroup handles both cases correctly), so
// in practice it shows only on an empty client list. Logged for a follow-up.
export const elasticLastColumnSx = {
  [LAST_DATA_COLUMN_SELECTOR]: {
    width: '100%',
  },
};

/** Fallback floor for a flex column that declares no `minWidth`. */
const DEFAULT_FLEX_MIN_WIDTH = 100;

/** The subset of a column definition that governs its width. */
export interface ColLayoutColumn {
  width?: number;
  flex?: number;
  minWidth?: number;
}

export interface ColLayout {
  /** One CSS width per column, in order, for the table's `<colgroup>`. */
  widths: string[];
  /** Sum of every column's floor — set as the table's `minWidth`. */
  minTableWidth: number;
}

/**
 * Builds the `<colgroup>` widths for a V4 list table under `table-layout: fixed`.
 * Columns with an explicit `width` keep their pixel size; the rest split the
 * remaining space by their `flex` weight and contribute their `minWidth` floor
 * to `minTableWidth`, so a table narrower than its floors scrolls horizontally
 * instead of squeezing its columns.
 *
 * Pair the result with `{ width: '100%', minWidth: minTableWidth,
 * tableLayout: 'fixed', ...stickyActionsTableSx }` — and NOT with
 * `elasticLastColumnSx`. A colgroup built here sizes every column, which already
 * fills the container, and the flex columns are percentage `calc()`s that the
 * stretch would collapse to 0px the moment one of them sorts last. See the note
 * on `elasticLastColumnSx`.
 *
 * TODO: four private copies of this predate the shared version and should be
 * consolidated onto it — StructureTable.tsx, UserTable.tsx, ShipmentTable.tsx
 * and ReplenishmentConfigGrid.tsx. Left alone deliberately: they render seven
 * shipped list pages between them, so the swap deserves its own change.
 */
export const buildColLayout = (cols: ColLayoutColumn[], actionsColWidth: number): ColLayout => {
  let fixedTotal = actionsColWidth;
  let flexTotal = 0;
  let minTableWidth = actionsColWidth;

  // A zero, negative or non-finite weight would divide by zero and emit a calc()
  // the browser discards, silently collapsing the column. Fall back to an even
  // share instead.
  const weightOf = (c: ColLayoutColumn): number => {
    const flex = c.flex ?? 1;
    return Number.isFinite(flex) && flex > 0 ? flex : 1;
  };

  for (const c of cols) {
    if (c.width != null) {
      fixedTotal += c.width;
      minTableWidth += c.width;
    } else {
      flexTotal += weightOf(c);
      minTableWidth += c.minWidth ?? DEFAULT_FLEX_MIN_WIDTH;
    }
  }

  const widths = cols.map((c) => {
    if (c.width != null) return `${c.width}px`;
    return `calc((100% - ${fixedTotal}px) * ${weightOf(c)} / ${flexTotal})`;
  });

  return { widths, minTableWidth };
};
